import { useMutation, useQuery } from '@tanstack/react-query'

import {
  api,
  type LoginPrecheckRequest,
  type LoginPrecheckResponse,
  type LoginRequest,
  type LoginResponse,
  type PrincipalResponse,
} from '@/lib/api/client'
import { useAuthStore } from '@/lib/auth/store'

export function useLogin() {
  const setSession = useAuthStore((s) => s.setSession)
  return useMutation({
    mutationFn: async (req: LoginRequest) => {
      const res = await api.post<LoginResponse>('/v1/auth/login', req)
      return res.data
    },
    onSuccess: (data) => {
      // totpPending: an interim, scope-limited access token with no
      // refresh token. Still worth persisting via setSession — the auth
      // interceptor attaches it like any other, letting the totp-setup
      // page's calls to /v1/auth/totp/* go through, and isTotpPending()
      // reads this exact shape (access present, refresh absent) to route
      // there instead of the dashboard.
      setSession({
        accessToken: data.accessToken,
        refreshToken: data.refreshToken,
        tenantSlug: data.tenantSlug,
        empId: data.empId,
      })
    },
  })
}

/**
 * Pre-check the login page uses to decide whether to show the TOTP field
 * before the password step. Called with tenant + empId only, before any
 * session exists — unauthenticated by design (see SecurityConfig permitAll).
 */
/** Always 202 — the API never says whether the account exists. */
export function useForgotPassword() {
  return useMutation({
    mutationFn: async (req: { tenantSlug: string; empId: string }) => {
      await api.post('/v1/auth/password/forgot', req)
    },
  })
}

/** The emailed token + a new password; 400 `reset_link_invalid` when used or expired. */
export function useResetPassword() {
  return useMutation({
    mutationFn: async (req: { tenantSlug: string; token: string; newPassword: string }) => {
      await api.post('/v1/auth/password/reset', req)
    },
  })
}

export function useLoginPrecheck() {
  return useMutation({
    mutationFn: async (req: LoginPrecheckRequest) => {
      const res = await api.post<LoginPrecheckResponse>('/v1/auth/login-precheck', req)
      return res.data
    },
  })
}

export function useMe() {
  const accessToken = useAuthStore((s) => s.accessToken)
  return useQuery({
    queryKey: ['auth', 'me'],
    enabled: !!accessToken,
    queryFn: async () => {
      const res = await api.get<PrincipalResponse>('/v1/auth/me')
      return res.data
    },
    staleTime: 60_000,
  })
}
